/**
 * Session-aware time math for the chart page — Phase 6a-1 commit 1 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A26 pure
 * subset). Function bodies moved VERBATIM from static/index.html; behaviour
 * must stay byte-identical.
 *
 * Time convention: candle times throughout the pages use the "+IST_OFFSET
 * shifted epoch" — the value is shifted so that UTC getters on it read out
 * IST wall-clock. Every function here taking a time expects that convention.
 *
 * Session bounds DERIVE from market_time's SESSION_*_MIN (decision ②,
 * 2026-08-04): one source of truth, mirroring the backend's canonical
 * aggregator/tick_guard.py MARKET_OPEN/MARKET_CLOSE. The next exchange
 * timing change is a one-line edit in market_time.ts.
 *
 * Deliberately NOT here (blueprint R8): patchSeriesWithFutureWhitespace —
 * it reads the page's bridged state.candleSeries and calls the chart-type
 * transforms; it stays in static/index.html.
 *
 * RESPONSIBILITY FROZEN (v2.10 ruling, commit 12): this module contains
 * deterministic trading-session mathematics ONLY. Additions are permitted
 * only if they are pure deterministic computation from explicit parameters
 * and the existing session constants — never chart rendering/handles/series,
 * DOM, timers, clocks (Date.now), browser APIs, page state, UI formatting,
 * hidden state, side effects, or avoidable allocation. Anything that fails
 * ANY of those requires a new blueprint amendment before landing here.
 */

import { NSE_TRADING_HOLIDAYS, SESSION_START_MIN, SESSION_END_MIN } from "../market_time";

/** Seconds per bar for each supported timeframe (calendar TFs approximate). */
export const TF_SECONDS: Record<string, number> = {
  "1m": 60, "3m": 180, "5m": 300, "15m": 900, "1h": 3600,
  "1D": 86400, "1W": 604800, "1M": 2592000,
};

export function getIntervalSeconds(tf: string): number {
  return TF_SECONDS[tf] || 300;
}

// 09:15 / 15:40 IST as seconds-of-day. Arithmetically identical to the old
// page literals (9*3600 + 15*60 and 15*3600 + 40*60) — pinned by tests.
export const SESSION_START_SEC = SESSION_START_MIN * 60;
export const SESSION_END_SEC = SESSION_END_MIN * 60;

// Generous — covers many sessions ahead once you scroll, independent of the
// (small) default rightOffset gap.
const FUTURE_WHITESPACE_COUNT = 400;

/**
 * `t` is already in the "+IST_OFFSET" shifted-epoch convention used
 * throughout the pages for candle times, so UTC getters on it read out IST
 * wall-clock values.
 */
export function dateStrFromShiftedEpoch(t: number): string {
  const d = new Date(t * 1000);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * The next bar time after `t` that falls inside a trading session — skipping
 * weekends, published NSE/BSE holidays, and outside-session hours the same
 * way a real feed would (jumping straight from Friday close to Monday 09:15,
 * or past a holiday entirely).
 */
export function nextSessionBarTime(t: number, intervalSec: number): number {
  let next = t + intervalSec;
  for (let guard = 0; guard < 30; guard++) {
    const d = new Date(next * 1000);
    const dow = d.getUTCDay();
    const secOfDay = d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds();
    const dayStart = Math.floor(next / 86400) * 86400;
    if (dow === 0 || dow === 6 || NSE_TRADING_HOLIDAYS.includes(dateStrFromShiftedEpoch(next))) {
      next = dayStart + 86400 + SESSION_START_SEC; continue; // weekend or holiday -> next calendar day, re-checked next loop
    }
    if (secOfDay >= SESSION_END_SEC) { next = dayStart + 86400 + SESSION_START_SEC; continue; }
    if (secOfDay < SESSION_START_SEC) { next = dayStart + SESSION_START_SEC; continue; }
    break;
  }
  return next;
}

/**
 * Previous trading day's close, derived from already-loaded candles — Phase
 * 6a-4 commit 12 (R26 pure seam; body verbatim from updatePdcLine's helper).
 * Finds the last trading-day boundary in the history and returns the close of
 * the candle just before it. Times carry the +IST_OFFSET shifted-epoch
 * convention, so a day boundary is just a change in Math.floor(time / 86400)
 * — the same test redrawOverlay() uses for session separators.
 */
export function previousDayCloseFromCandles(
  candles: Array<{ time: number; close: number }> | null | undefined,
): number | null {
  if (!candles || candles.length < 2) return null;
  for (let i = candles.length - 1; i > 0; i--) {
    if (Math.floor(candles[i].time / 86400) !== Math.floor(candles[i - 1].time / 86400)) {
      return candles[i - 1].close;
    }
  }
  return null; // loaded history doesn't span more than one trading day
}

/**
 * Seconds until the current session bar closes, or null outside session
 * hours — Phase 6a-4 commit 12 (R26 pure seam; arithmetic verbatim from
 * startCountdown). Candles are anchored to the 9:15 IST session open, NOT to
 * epoch boundaries (the backend's _floor_to_interval) — the countdown must
 * use the same grid or it's wrong for any interval that doesn't divide
 * evenly into the epoch (1h candles close at :15; a naive `now % interval`
 * counted down to :00 — the fixed live bug this preserves the fix for).
 * `intervalSec` is a parameter (not a tf string) to preserve the page
 * shell's capture-once-per-startCountdown contract. The caller supplies
 * istSecOfDay — this module stays clock-free.
 */
export function sessionCountdown(istSecOfDay: number, intervalSec: number): number | null {
  if (istSecOfDay < SESSION_START_SEC || istSecOfDay >= SESSION_END_SEC) {
    return null; // outside session hours no candle is forming
  }
  const sinceOpen = istSecOfDay - SESSION_START_SEC;
  const effInterval = Math.min(intervalSec, SESSION_END_SEC - SESSION_START_SEC);
  // The day's final candle closes at 15:40 even if the interval says later.
  return Math.min(
    effInterval - (sinceOpen % effInterval),
    SESSION_END_SEC - istSecOfDay,
  );
}

/**
 * The visible time-range for one session's candles — Phase 6a-4 commit 13
 * (R27 pure seam; index-window arithmetic verbatim from
 * scrollToSessionByLogicalRange). Returns null when NO candle falls inside
 * the session (e.g. a holiday with no data) — the page shell falls back to
 * the synthetic clock-time bounds. Padding: a few REAL candles on the left —
 * bleeding a little into the previous session's close is harmless (it's
 * real, valid data, and just looks like a normal zoomed-out view). The right
 * side is different: the candle array has no gap in its INDEXING between
 * sessions, only in time, so lastIdx+1/+2/+3 are simply the NEXT trading
 * day's first few candles — padding the end by array position alone silently
 * pulls the following day into view, right back to "picking 2 July shows
 * 2 July AND 3 July". Only extend the right side while the next candle is
 * still inside THIS session (<= sessionEnd); the moment it isn't, stop, so
 * the picked day's own last candle stays the actual right edge.
 */
export function sessionVisibleRange(
  candles: Array<{ time: number }>,
  sessionStart: number,
  sessionEnd: number,
): { from: number; to: number } | null {
  let firstIdx = -1, lastIdx = -1;
  for (let i = 0; i < candles.length; i++) {
    const t = candles[i].time;
    if (t >= sessionStart && t <= sessionEnd) {
      if (firstIdx === -1) firstIdx = i;
      lastIdx = i;
    }
  }
  if (firstIdx === -1) return null;
  const pad = 3;
  const padStartIdx = Math.max(0, firstIdx - pad);
  let padEndIdx = lastIdx;
  for (let i = lastIdx + 1; i <= Math.min(candles.length - 1, lastIdx + pad); i++) {
    if (candles[i].time <= sessionEnd) padEndIdx = i; else break;
  }
  return { from: candles[padStartIdx].time, to: candles[padEndIdx].time };
}

/**
 * Resolve a picked date to itself if it's already a trading day, or roll
 * FORWARD to the next one that is — Phase 6a-4 commit 13 (R27; body
 * verbatim). Mirrors the weekend/holiday-skipping used for future whitespace
 * (nextSessionBarTime), just at day granularity instead of bar granularity.
 * NB: deliberately uses UTC getters on a 'T00:00:00Z' parse (unlike
 * market_time's local-getter walkers) — preserved exactly.
 */
export function resolveToTradingDay(dateStr: string): string {
  let d = new Date(dateStr + 'T00:00:00Z');
  for (let guard = 0; guard < 14; guard++) {
    const dow = d.getUTCDay();
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const ds = `${y}-${m}-${day}`;
    if (dow !== 0 && dow !== 6 && !NSE_TRADING_HOLIDAYS.includes(ds)) return ds;
    d = new Date(d.getTime() + 86400000);
  }
  return dateStr; // shouldn't be reachable — 14 days always crosses a trading day
}

/**
 * Appends session-aware future-whitespace points ({time}-only) after the last
 * real candle, so lightweight-charts renders real date/time labels when
 * scrolling right (rightOffset alone only reserves empty pixels — the axis
 * labels nothing it doesn't have a data point for).
 */
export function appendFutureWhitespace(
  data: Array<{ time: number }>,
  tf: string,
): Array<{ time: number }> {
  if (!data || !data.length) return data;
  const last = data[data.length - 1];
  if (!last || last.time == null) return data;
  const intervalSec = getIntervalSeconds(tf);
  const points: Array<{ time: number }> = new Array(FUTURE_WHITESPACE_COUNT);
  let t = last.time;
  for (let i = 0; i < FUTURE_WHITESPACE_COUNT; i++) {
    t = nextSessionBarTime(t, intervalSec);
    points[i] = { time: t };
  }
  return data.concat(points);
}
